"use client";

import { useRef } from "react";
import IsoSceneStage from "@/components/iso/IsoSceneStage";
import { GradText, SectionHead, SectionShell, useBandRowAlign, useRefGridMode, useWholeRowBlocks } from "./kit";
import { INTEGRATIONS_SCENE } from "./integrations-iso";

/**
 * "Connects to your code" — the isometric fan-out: the rii.link routing core
 * wired to what ships today — the live click stream, email forwarding, the
 * MCP server and the REST API — pulses riding the connectors. No third-party
 * connector is drawn: none exists (claims audit 2026-09-27).
 * Replaced the 12-tile wall (2026-08-10 iso-scene-reuse spec); the full
 * catalog lives on /integrations via the SectionCta the homepage mounts
 * below this section. The scene floats on the body grid — hairlines stay
 * visible through it (grid-paper look); only the SectionHead clears.
 */
export default function IntegrationsSection() {
  const sceneWrapRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef<HTMLDivElement>(null); // never attached — disables the hooks off-grid
  const onGrid = useRefGridMode();

  // Row-align the SectionHead block on the homepage grid; both hooks locate
  // [data-grid-block]s from the enclosing <section>, so any in-section ref
  // works as the anchor. Off-grid (/solutions/marketing) they must no-op —
  // hooks can't be called conditionally, so point them at the never-attached
  // ref there (the established gating pattern from the tile-wall version).
  useWholeRowBlocks(onGrid ? sceneWrapRef : nullRef);
  useBandRowAlign(onGrid ? sceneWrapRef : nullRef);

  return (
    <SectionShell variant="tight" divider>
      <SectionHead
        center
        eyebrow="Connects to your code"
        title={
          <>
            Plug rii.link into <GradText>your code and agents.</GradText>
          </>
        }
      />

      {/* data-deco-avoid: SectionDecorations must stay out of the scene's
          cells. NOT a [data-grid-block] — the illustration keeps the grid
          visible behind it. */}
      <div ref={sceneWrapRef} data-deco-avoid className="relative">
        <p className="sr-only">
          Illustration: the rii.link routing core connected to a live click
          stream (for links created through the API), email forwarding to your inbox, Claude via MCP and the REST
          API, with requests pulsing along the connections.
        </p>
        <IsoSceneStage
          {...INTEGRATIONS_SCENE}
          surfaceRef={sceneWrapRef}
          className="pointer-events-none mx-auto w-full max-w-[560px] md:max-w-[1000px]"
        />
      </div>
    </SectionShell>
  );
}
